import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface TrimWhitespaceTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  fileData: string[];
  onApplyTransformation: (config: any) => void;
}

const TrimWhitespaceTransformationDialog: React.FC<
  TrimWhitespaceTransformationDialogProps
> = ({ isOpen, onClose, targetField, fileData, onApplyTransformation }) => {
  const [formData, setFormData] = useState({
    trimType: "both",
    alsoRemove: [],
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samplesWithWhitespace = fileData
        .filter((s) => s && s !== "...")
        .slice(0, 2)
        .map((s) => `  ${s}  `);

      setPreviewData({
        before: samplesWithWhitespace,
        after: samplesWithWhitespace,
      });
      updatePreview(
        { trimType: "both", alsoRemove: [] },
        samplesWithWhitespace
      );
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "trimType",
      type: "radio",
      label: "Trim type",
      options: [
        { value: "both", label: "Both sides" },
        { value: "leading", label: "Leading only" },
        { value: "trailing", label: "Trailing only" },
      ],
      defaultValue: "both",
      className: "col-span-1",
    },
    {
      name: "alsoRemoveHeader",
      type: "custom",
      className: "col-span-full",
      customRender: () => (
        <div>
          <span className="text-sm font-semibold text-gray-700">
            Also remove
          </span>
        </div>
      ),
    },
    {
      name: "removeExtraSpaces",
      type: "checkbox",
      label: "Extra spaces between words",
      className: "col-span-1",
    },
    {
      name: "removeTabs",
      type: "checkbox",
      label: "Tabs",
      className: "col-span-1",
    },
    {
      name: "removeLineBreaks",
      type: "checkbox",
      label: "Line breaks",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    let newFormData = { ...formData };

    // Handle checkbox fields specially
    if (
      fieldName === "removeExtraSpaces" ||
      fieldName === "removeTabs" ||
      fieldName === "removeLineBreaks"
    ) {
      const alsoRemove = [...(formData.alsoRemove || [])];

      if (fieldName === "removeExtraSpaces") {
        if (value) {
          if (!alsoRemove.includes("extra_spaces"))
            alsoRemove.push("extra_spaces");
        } else {
          const index = alsoRemove.indexOf("extra_spaces");
          if (index > -1) alsoRemove.splice(index, 1);
        }
      }
      if (fieldName === "removeTabs") {
        if (value) {
          if (!alsoRemove.includes("tabs")) alsoRemove.push("tabs");
        } else {
          const index = alsoRemove.indexOf("tabs");
          if (index > -1) alsoRemove.splice(index, 1);
        }
      }
      if (fieldName === "removeLineBreaks") {
        if (value) {
          if (!alsoRemove.includes("line_breaks"))
            alsoRemove.push("line_breaks");
        } else {
          const index = alsoRemove.indexOf("line_breaks");
          if (index > -1) alsoRemove.splice(index, 1);
        }
      }

      newFormData = { ...formData, alsoRemove };
    } else {
      newFormData = { ...formData, [fieldName]: value };
    }

    setFormData(newFormData);
    updatePreview(newFormData, previewData.before);
  };

  const updatePreview = (data: any, samples: string[]) => {
    const results = samples.map((item) => {
      let result = item;

      switch (data.trimType) {
        case "both":
          result = result.trim();
          break;
        case "leading":
          result = result.trimStart();
          break;
        case "trailing":
          result = result.trimEnd();
          break;
      }

      // Check if alsoRemove exists and contains the values
      if (data.alsoRemove?.includes("extra_spaces"))
        result = result.replace(/\s+/g, " ");
      if (data.alsoRemove?.includes("tabs"))
        result = result.replace(/\t/g, " ");
      if (data.alsoRemove?.includes("line_breaks"))
        result = result.replace(/(\r\n|\n|\r)/g, " ");

      return result;
    });

    setPreviewData({ before: samples, after: results });
  };

  const handleApply = () => {
    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "trim_whitespace",
        order: 1,
        config: {
          trim_type: formData.trimType,
          ...(formData.alsoRemove &&
            formData.alsoRemove.length > 0 && {
              also_remove: formData.alsoRemove,
            }),
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Trim Whitespace Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        {/* Info */}
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Trim Whitespace:</strong> Clean up data entry errors, remove
            leading/trailing spaces, and normalize spacing.
          </div>
        </div>

        {/* Form */}
        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Preview */}
        <div className="border rounded-lg p-4 bg-gray-50 relative">
          <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
          <div className="grid grid-cols-2 gap-6 relative">
            {/* Before */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">Before</p>
              <div className="space-y-2">
                {previewData.before.length > 0 ? (
                  previewData.before.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800 whitespace-pre">
                        {item}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No data available
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2">
              <span className="text-gray-400 text-lg">→</span>
            </div>

            {/* After */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">After</p>
              <div className="space-y-2">
                {previewData.after.length > 0 ? (
                  previewData.after.map((item, idx) => (
                    <div
                      key={idx}
                      className="bg-white p-3 rounded-md border border-gray-200"
                    >
                      <span className="text-sm text-gray-800">{item}</span>
                    </div>
                  ))
                ) : (
                  <div className="bg-white p-3 rounded-md border border-gray-200">
                    <span className="text-sm text-gray-400">
                      No preview available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Transformation</Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default TrimWhitespaceTransformationDialog;
